import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatWIB } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ThumbsUp, HelpCircle, AlertTriangle, CheckCircle2, CornerDownRight, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const REACTIONS = [
  { key: "setuju", icon: ThumbsUp, label: "Setuju" },
  { key: "klarifikasi", icon: HelpCircle, label: "Perlu Klarifikasi" },
  { key: "keberatan", icon: AlertTriangle, label: "Keberatan" },
];

function initials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

export default function CommentThread({ entityType, entityId, sectionKey = null, compact = false }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/comments", { params: { entity_type: entityType, entity_id: entityId } });
    setComments(sectionKey ? data.filter((c) => c.section_key === sectionKey) : data);
  };
  useEffect(() => { if (entityId) load(); }, [entityId, sectionKey]);

  const post = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await api.post("/comments", {
        entity_type: entityType, entity_id: entityId, text, section_key: sectionKey,
        parent_comment_id: replyTo,
      });
      setText(""); setReplyTo(null);
      await load();
    } catch (e) { toast.error("Gagal mengirim komentar"); }
    setLoading(false);
  };

  const react = async (cid, reaction) => {
    await api.post(`/comments/${cid}/react`, { reaction });
    load();
  };
  const resolve = async (cid) => {
    await api.post(`/comments/${cid}/resolve`);
    load();
  };

  const roots = comments.filter((c) => !c.parent_comment_id);
  const childrenOf = (id) => comments.filter((c) => c.parent_comment_id === id);

  const renderComment = (c, isChild = false) => (
    <div key={c.id} className={cn("flex gap-3", isChild && "ml-8 mt-2")} data-testid="comment-item">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-bold">{initials(c.author_name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-800">{c.author_name}</span>
            <span className="text-xs text-slate-400">{formatWIB(c.created_at)}</span>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap mt-0.5">{c.text}{c.edited && <span className="text-xs text-slate-400 ml-1">(diedit)</span>}</p>
        </div>
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {REACTIONS.map((r) => {
            const count = (c.reactions?.[r.key] || []).length;
            const active = (c.reactions?.[r.key] || []).includes(user?.id);
            const Icon = r.icon;
            return (
              <button key={r.key} onClick={() => react(c.id, r.key)} data-testid={`comment-react-${r.key}`}
                className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition-colors",
                  active ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-slate-200 text-slate-500 hover:bg-slate-50")}>
                <Icon className="h-3 w-3" />{count > 0 && count}
              </button>
            );
          })}
          {!isChild && (
            <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)} className="text-xs text-slate-500 hover:text-indigo-600 px-2 py-0.5">
              <CornerDownRight className="h-3 w-3 inline mr-0.5" />Balas
            </button>
          )}
          <button onClick={() => resolve(c.id)} className={cn("text-xs px-2 py-0.5 inline-flex items-center gap-0.5", c.is_resolved ? "text-emerald-600" : "text-slate-500 hover:text-emerald-600")}>
            <CheckCircle2 className="h-3 w-3" />{c.is_resolved ? "Terselesaikan" : "Tandai selesai"}
          </button>
        </div>
        {childrenOf(c.id).map((ch) => renderComment(ch, true))}
        {replyTo === c.id && (
          <div className="ml-8 mt-2 flex gap-2">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Tulis balasan..." rows={2}
              className="text-sm" data-testid="comment-reply-textarea" />
            <Button size="sm" onClick={post} disabled={loading} data-testid="comment-reply-post"><Send className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {roots.length === 0 && <p className="text-sm text-slate-400 italic">Belum ada komentar.</p>}
      <div className="space-y-3">{roots.map((c) => renderComment(c))}</div>
      {!replyTo && (
        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Tulis komentar..." rows={compact ? 1 : 2}
            className="text-sm" data-testid="comment-input-textarea" />
          <Button onClick={post} disabled={loading} data-testid="comment-post-button" className="self-end"><Send className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
